import { isPermissionsEnabled, PERMISSION_ACTIONS } from '~/components/core/Permissions/PermissionUtils';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { getIsCatSupportedForOrganization, isFeatureEnabled } from '~/Utils';

/**
 * Organization-level availability of each sysconfig surface (an Operations tile or
 * a customer-facing tab), shared between OperationsTab's tile gating and the
 * permission-profile screen's scope-row visibility so the two cannot drift: a
 * granular scope row is offered on a profile exactly when the organization can
 * reach at least one surface that scope gates.
 *
 * The super-org-guarded Dev Tools tab has no granular scope at all: it rides the
 * parent SYSTEM_CONFIGURATION entry permission, with its audience condition kept
 * on the card (`isEnabledForUser` in OperationsTab). The Performance QA builder
 * has a scope, but its tile is also visible to org-level supervisors without it
 * (`orIsVisibleForUser` in OperationsTab) — its scope row stays offered on every
 * profile regardless.
 */

/**
 * The only operational-details fields allowed to drive availability. Predicates
 * read through this picker, so memo keys built from it (usePermissionItemVisibility,
 * usePermissionProfileValidationSchema) always cover every field the predicates
 * consume — a predicate cannot quietly grow a dependency the keys miss.
 */
export const pickAvailabilityOperationalDetails = (organizationOperationalDetails) => ({
  isMetabaseEnabled: Boolean(organizationOperationalDetails?.isMetabaseEnabled),
  isMetabaseReportingConfigured: Boolean(organizationOperationalDetails?.isMetabaseReportingConfigured),
  isNavigatorEnabled: Boolean(organizationOperationalDetails?.isNavigatorEnabled),
  isClaimSearchEnabled: Boolean(organizationOperationalDetails?.isClaimSearchEnabled),
  isCccEnabled: Boolean(organizationOperationalDetails?.isCccEnabled),
  isTableauAccountsEnabled: Boolean(
    organizationOperationalDetails?.tableauAccountsConfiguration?.is_tableau_accounts_enabled
  ),
});

const always = () => true;

export const SURFACE_ORG_AVAILABILITY = {
  always,
  subOrganizations: ({ organization }) => Boolean(organization.sub_organizations_enabled),
  catEvents: ({ organization }) => getIsCatSupportedForOrganization(organization),
  dataReporting: ({ organization, organizationOperationalDetails }) => {
    const details = pickAvailabilityOperationalDetails(organizationOperationalDetails);
    return (
      isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.SCHEDULED_DATA_EXPORT) ||
      details.isMetabaseReportingConfigured ||
      details.isMetabaseEnabled
    );
  },
  navigator: ({ organizationOperationalDetails }) =>
    pickAvailabilityOperationalDetails(organizationOperationalDetails).isNavigatorEnabled,
  claimSearch: ({ organizationOperationalDetails }) =>
    pickAvailabilityOperationalDetails(organizationOperationalDetails).isClaimSearchEnabled,
  ccc: ({ organizationOperationalDetails }) =>
    pickAvailabilityOperationalDetails(organizationOperationalDetails).isCccEnabled,
  permissionsProfiles: ({ organization }) => isPermissionsEnabled(organization),
  devTools: ({ organization }) => isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.EXTERNAL_DEV_TOOLS),
  policyAndFnolConfiguration: ({ organization }) =>
    isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.CONFIGURABLE_FNOL),
  tableauAccounts: ({ organization, organizationOperationalDetails }) =>
    isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.TABLEAU_ACCOUNTS) &&
    !isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.METABASE_APPROVED) &&
    pickAvailabilityOperationalDetails(organizationOperationalDetails).isTableauAccountsEnabled,
  configurationManagement: ({ organization }) =>
    isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.CONFIGURATION_MANAGEMENT) ||
    isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.STARR_DEMO_AUDIT_LOG),
  delegatedAuthority: ({ organization }) =>
    isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.DELEGATED_AUTHORITY),
  globalConfiguredFields: ({ organization }) =>
    isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.GLOBAL_CONFIGURED_FIELDS),
  systemOfRecord: ({ organization }) => isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.SYSTEM_OF_RECORD),
};

/**
 * Every granular scope with the availability of each surface it gates. The User /
 * Adjuster Management tabs (routes.tsx) and the always-on tiles are `always`; a
 * scope no surface gates yet maps to an empty list and is never offered.
 * A test walks OPERATIONS_CARDS and adminCustomerFacingTabs to pin this map to the
 * surfaces' own predicates, so the association cannot drift from the tiles.
 */
export const SYSCONFIG_SCOPE_SURFACES = {
  [PERMISSION_ACTIONS.SYSCONFIG_USER_MANAGEMENT]: [always],
  [PERMISSION_ACTIONS.SYSCONFIG_ADJUSTER_MANAGEMENT]: [always],
  [PERMISSION_ACTIONS.SYSCONFIG_PERFORMANCE_QA]: [always],
  [PERMISSION_ACTIONS.SYSCONFIG_SUB_ORGANIZATIONS]: [SURFACE_ORG_AVAILABILITY.subOrganizations],
  [PERMISSION_ACTIONS.SYSCONFIG_LOGIN]: [always],
  [PERMISSION_ACTIONS.SYSCONFIG_CAT_EVENTS]: [SURFACE_ORG_AVAILABILITY.catEvents],
  [PERMISSION_ACTIONS.SYSCONFIG_AUTOMATION]: [always],
  [PERMISSION_ACTIONS.SYSCONFIG_DATA_REPORTING]: [
    SURFACE_ORG_AVAILABILITY.dataReporting,
    SURFACE_ORG_AVAILABILITY.tableauAccounts,
  ],
  [PERMISSION_ACTIONS.SYSCONFIG_INTEGRATIONS]: [
    SURFACE_ORG_AVAILABILITY.navigator,
    SURFACE_ORG_AVAILABILITY.claimSearch,
    SURFACE_ORG_AVAILABILITY.ccc,
  ],
  [PERMISSION_ACTIONS.SYSCONFIG_LOCATIONS]: [always],
  [PERMISSION_ACTIONS.SYSCONFIG_COMMUNICATIONS]: [always],
  [PERMISSION_ACTIONS.SYSCONFIG_PERMISSIONS]: [SURFACE_ORG_AVAILABILITY.permissionsProfiles],
  [PERMISSION_ACTIONS.SYSCONFIG_CLAIM_CONFIG]: [always],
  [PERMISSION_ACTIONS.SYSCONFIG_POLICY_FNOL]: [SURFACE_ORG_AVAILABILITY.policyAndFnolConfiguration],
  [PERMISSION_ACTIONS.SYSCONFIG_TEMPLATES]: [always],
  [PERMISSION_ACTIONS.SYSCONFIG_CONTACT]: [always],
  [PERMISSION_ACTIONS.SYSCONFIG_RESERVES]: [always],
  [PERMISSION_ACTIONS.SYSCONFIG_NOTIFICATIONS]: [always],
  [PERMISSION_ACTIONS.SYSCONFIG_CONFIG_MANAGEMENT]: [SURFACE_ORG_AVAILABILITY.configurationManagement],
  [PERMISSION_ACTIONS.SYSCONFIG_DELEGATED_AUTHORITY]: [SURFACE_ORG_AVAILABILITY.delegatedAuthority],
  [PERMISSION_ACTIONS.SYSCONFIG_CHAT_SUGGESTIONS]: [always],
  [PERMISSION_ACTIONS.SYSCONFIG_GLOBAL_FIELDS]: [SURFACE_ORG_AVAILABILITY.globalConfiguredFields],
  [PERMISSION_ACTIONS.SYSCONFIG_SYSTEM_OF_RECORD]: [SURFACE_ORG_AVAILABILITY.systemOfRecord],
};

/**
 * Whether the organization can use the area a granular sysconfig scope gates.
 * Non-scope action ids are not availability-gated and always return true.
 */
export const isSysconfigScopeAvailableForOrganization = (
  actionId,
  { organization, organizationOperationalDetails }
) => {
  const surfaces = SYSCONFIG_SCOPE_SURFACES[actionId];
  if (!surfaces) {
    return true;
  }
  return surfaces.some((isSurfaceAvailable) => isSurfaceAvailable({ organization, organizationOperationalDetails }));
};
